import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
// `@segment/analytics-next` is pinned exactly in the root package.json. XCC checkout loads Segment from an
// unversioned CDN URL that Segment updates on its own; before bumping, check the live build with
// `window.analytics.VERSION` on checkout so both sites resolve anonymousId alike.
import type { AnalyticsBrowser, AnalyticsBrowserSettings, InitOptions } from '@segment/analytics-next';
import { AD_OPT_OUT_INTEGRATIONS, CONSENT_OPT_OUT_VALUE, createAdOptOutMiddleware } from '@ecom/common';
import { EnvService } from '../env/env.service';
import { ScriptLoaderService } from '../script-loader/script-loader.service';
import { AnalyticsConsentService } from './analytics-consent.service';
import { HTTPS_PROTOCOL, SEGMENT_CONSENT_EVENT, SEGMENT_INTEGRATION_NAME } from './analytics.constants';
import type { AnalyticsPageData, AnalyticsPageOptions } from './analytics.types';

/**
 * Handles initialization of Segment Analytics 2.0 with Signals plugin.
 * Focuses solely on analytics SDK initialization and configuration.
 *
 * @example
 *   // Initialize analytics
 *   await analyticsInitializer.initialize();
 *
 *   // Access analytics instance
 *   const analytics = analyticsInitializer.analytics;
 *   analytics.track('Event Name', { property: 'value' });
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsInitializerService {
  private analyticsInstance: AnalyticsBrowser | null = null;
  private initialized = false;

  constructor(
    private readonly envService: EnvService,
    private readonly consentService: AnalyticsConsentService,
    private readonly scriptLoaderService: ScriptLoaderService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  /**
   * Initialize Segment Analytics 2.0 with Signals plugin
   * @param writeKeyOverride Optional write key override (defaults to environment config)
   * @returns Promise that resolves when analytics is loaded
   */
  async initialize(writeKeyOverride?: string): Promise<void> {
    if (!this.shouldInitialize(writeKeyOverride)) {
      return;
    }

    // Check feature flag - if false/undefined, use traditional script injection
    if (!this.envService.get.brandConfig?.use_segment_signals) {
      this.scriptLoaderService.injectScriptByType('segment', writeKeyOverride);
      this.initialized = true;
      return;
    }

    try {
      const writeKey = writeKeyOverride || this.envService.get.segmentId;
      // Persist a GPC opt-out before Segment loads, so the load options and the first page() already
      // carry it and no ad destination is ever instantiated for this visitor.
      const gpcOptOutApplied = this.applyGlobalPrivacyControl();
      await this.loadSegmentLibraries();
      await this.registerSignalsPlugin();
      await this.loadAnalytics(writeKey);
      this.initialized = true;
      this.sendInitialPageCall(gpcOptOutApplied);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Segment Analytics] Initialization failed:', error);
      throw error;
    }
  }

  /**
   * Get the analytics instance
   * @returns AnalyticsBrowser instance or null if not initialized
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get analytics(): any {
    return this.analyticsInstance;
  }

  /**
   * Check if analytics has been initialized
   */
  get isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Check if initialization should proceed
   */
  private shouldInitialize(writeKeyOverride?: string): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    if (this.initialized) {
      return false;
    }

    const writeKey = writeKeyOverride || this.envService.get.segmentId;
    return !!writeKey;
  }

  /**
   * Dynamically load Segment libraries
   */
  private async loadSegmentLibraries(): Promise<void> {
    const [{ AnalyticsBrowser }] = await Promise.all([
      import('@segment/analytics-next'),
      import('@segment/analytics-signals'),
    ]);

    this.analyticsInstance = new AnalyticsBrowser();
    // Re-check the opt-out on every event, so Signals/Event Builder events, direct instance calls and
    // opt-outs made mid-session are enforced too, not only calls routed through SegmentService.
    // Not awaited: before load() the call is buffered and resolves only once load() runs, and load()
    // installs buffered middleware before processing any event. The catch keeps a failure handled.
    this.analyticsInstance
      .addSourceMiddleware(createAdOptOutMiddleware(() => this.consentService.isOptedOut()))
      .catch(() => undefined);
  }

  /**
   * Register Signals plugin with analytics instance
   */
  private async registerSignalsPlugin(): Promise<void> {
    try {
      const { SignalsPlugin } = await import('@segment/analytics-signals');
      const signalsPlugin = new SignalsPlugin();
      void this.analyticsInstance?.register(signalsPlugin);
    } catch (signalsError) {
      // eslint-disable-next-line no-console
      console.error('[Segment Analytics] Failed to initialize Signals plugin:', signalsError);
    }
  }

  /**
   * Load analytics with configuration
   */
  private async loadAnalytics(writeKey: string): Promise<void> {
    await this.analyticsInstance?.load(this.createLoadSettings(writeKey), this.createInitOptions());
  }

  /**
   * Create load settings (first `load()` argument) with the CDN proxy
   */
  private createLoadSettings(writeKey: string): AnalyticsBrowserSettings {
    const settings: AnalyticsBrowserSettings = { writeKey };

    const cdnProxy = this.envService.get.segmentCdnProxy;
    if (cdnProxy?.length > 0) {
      settings.cdnURL = `${HTTPS_PROTOCOL}://${cdnProxy}`;
    }

    return settings;
  }

  /**
   * Create init options (second `load()` argument) with the API proxy, and with every ad
   * destination switched off for opted-out visitors so analytics-next never instantiates them.
   * analytics-next reads `integrations` only from this argument; on the first it is ignored
   * and events fall back to api.segment.io.
   */
  private createInitOptions(): InitOptions {
    const integrations: NonNullable<InitOptions['integrations']> = this.consentService.isOptedOut()
      ? { ...AD_OPT_OUT_INTEGRATIONS }
      : {};

    const apiProxy = this.envService.get.segmentApiProxy;
    if (apiProxy) {
      integrations[SEGMENT_INTEGRATION_NAME] = {
        apiHost: `${apiProxy}/v1`,
        protocol: HTTPS_PROTOCOL,
      };
    }

    return Object.keys(integrations).length > 0 ? { integrations } : {};
  }

  /**
   * Send initial page call with consent information
   * @param gpcOptOutApplied True when this load persisted a new GPC opt-out, which is announced
   *   with a consent event after the page call
   */
  private sendInitialPageCall(gpcOptOutApplied: boolean): void {
    if (!this.analyticsInstance) {
      return;
    }

    const pageData = this.createPageData();
    const pageOptions = this.createPageOptions();

    void this.analyticsInstance.page(pageData, pageOptions);

    if (gpcOptOutApplied) {
      this.fireConsentEvent();
    }
  }

  /**
   * Create page data for tracking
   */
  private createPageData(): AnalyticsPageData {
    return {
      url: window.location.href,
      path: window.location.pathname,
    };
  }

  /**
   * Create event options with consent preferences. Opted-out visitors also get every ad
   * destination switched off for the event (cloud-mode delivery included).
   */
  private createPageOptions(): AnalyticsPageOptions {
    const { dataSharing } = this.consentService.getConsentPreferences();

    return {
      consent: {
        categoryPreferences: {
          DataSharing: dataSharing,
        },
      },
      ...(dataSharing ? {} : { integrations: { ...AD_OPT_OUT_INTEGRATIONS } }),
    };
  }

  /**
   * Persist the opt-out cookie when GPC is on and the stored choice is not already the opt-out.
   * @returns True when this call changed the stored choice to the opt-out
   */
  private applyGlobalPrivacyControl(): boolean {
    if (!this.consentService.hasGlobalPrivacyControl()) {
      return false;
    }

    const { cookieValue } = this.consentService.getConsentPreferences();
    this.consentService.handleGlobalPrivacyControl();
    return cookieValue !== CONSENT_OPT_OUT_VALUE;
  }

  /**
   * Fire consent preference update event
   */
  private fireConsentEvent(): void {
    if (!this.analyticsInstance) {
      return;
    }

    void this.analyticsInstance.track(SEGMENT_CONSENT_EVENT, {}, this.createPageOptions());
  }
}
